import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import {
  listRegistryVehicles,
  parseTokenClaims,
  RegistryVehicle,
} from "../api";

// Istanbul default center; live positions will be wired in once
// vehicle-service exposes a "last known location" query endpoint backed by the
// InfluxDB telemetry stream. For now the page renders the registry vehicles
// and warns that positions are pending.
const DEFAULT_CENTER: [number, number] = [41.015137, 28.97953];
const DEFAULT_ZOOM = 11;

// Leaflet's default marker icons point to assets relative to the bundler
// (which they aren't in a Vite build), so use the CDN URLs explicitly.
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export function MapPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const myFirm = claims?.firm_id ?? "";
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [vehicles, setVehicles] = useState<RegistryVehicle[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = L.map(mapContainer.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    listRegistryVehicles(myFirm)
      .then(setVehicles)
      .catch((exc) => setError(String(exc)));
  }, [myFirm]);

  // Placeholder: scatter vehicle markers around Istanbul so the panel renders
  // something while the telemetry-backed last-position endpoint is being built.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || vehicles.length === 0) return;
    const markers: L.Marker[] = [];
    vehicles.forEach((v, i) => {
      const lat = DEFAULT_CENTER[0] + (i % 5) * 0.01;
      const lng = DEFAULT_CENTER[1] + Math.floor(i / 5) * 0.01;
      const marker = L.marker([lat, lng], { icon: defaultIcon }).addTo(map);
      marker.bindPopup(
        `<strong>${v.plate ?? v.id}</strong><br/>${v.id}<br/>${t("map.popupLocation")}`,
      );
      markers.push(marker);
    });
    return () => {
      markers.forEach((m) => m.remove());
    };
  }, [vehicles, t]);

  return (
    <div className="page">
      <h2>{t("map.title")}</h2>
      <p className="muted">
        {t("map.info", { firm: myFirm, count: vehicles.length })}
      </p>
      {error && <p className="error">{error}</p>}
      <div ref={mapContainer} className="map" />
    </div>
  );
}
